import { inject, Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { fromEvent, merge, Subject, Subscription, timer } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import environment from '@environments/environment';
import { SessionExpiredDialogComponent } from './session-expired-dialog/session-expired-dialog.component';
import { SessionTimeoutDialogComponent } from './session-timeout-dialog/session-timeout-dialog.component';
import { AppInsightsService } from '@data-access/shared';

@Injectable({
  providedIn: 'root',
})
export class SessionTimerService implements OnDestroy {
  private readonly _router = inject(Router);
  private readonly _dialog = inject(MatDialog);
  private readonly _appInsights = inject(AppInsightsService);

  private _destroy$ = new Subject<void>();
  private _userActivity$ = new Subject<void>();
  private _maxSessionTimer?: Subscription;
  private _idleTimer?: Subscription;

  public keepSessionAlive(): void {
    this._resetIdleTimer();
  }

  /**
   * Lifecycle Hook Start
   */
  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();

    if (this._maxSessionTimer) {
      this._maxSessionTimer.unsubscribe();
      this._maxSessionTimer = undefined;
    }

    if (this._idleTimer) {
      this._idleTimer.unsubscribe();
      this._idleTimer = undefined;
    }
  }
  /**
   * Lifecycle Hook End
   */
  startIdleTimers(): void {
    // Check if timers are already running
    if (this._maxSessionTimer || this._idleTimer) {
      console.debug('Session timers are already running');
      return;
    }

    // Start the max session timer
    this._startMaxSessionTimer();

    // Start monitoring user activity
    this._initializeActivityMonitoring();

    // Start the idle timer
    this._resetIdleTimer();
  }

  stopIdleTimers(): void {
    this.ngOnDestroy();
  }

  private _initializeActivityMonitoring(): void {
    // Clean up existing destroy$ subject if it exists
    if (this._destroy$) {
      this._destroy$.next();
      this._destroy$.complete();
    }

    // Create new subject for this monitoring session
    this._destroy$ = new Subject<void>();

    // Monitor mouse and keyboard events
    const activity$ = merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'mousedown'),
      fromEvent(document, 'mouseup'),
      fromEvent(document, 'click'),
      fromEvent(document, 'dblclick'),
      fromEvent(document, 'wheel'),
      fromEvent(document, 'scroll'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'keyup'),
      fromEvent(document, 'touchstart'),
      fromEvent(document, 'touchmove'),
      fromEvent(document, 'touchend'),
      fromEvent(document, 'input'),
      fromEvent(document, 'focus'),
    ).pipe(
      debounceTime(1000), // Prevent excessive emissions
      takeUntil(this._destroy$),
    );

    activity$.subscribe(() => {
      this._userActivity$.next();
      this._resetIdleTimer();
    });
  }

  private _startMaxSessionTimer(): void {
    if (this._maxSessionTimer) {
      this._maxSessionTimer.unsubscribe();
    }

    this._maxSessionTimer = timer(environment.maxSessionLength)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        const dialogRef = this._dialog.open(SessionExpiredDialogComponent, {
          disableClose: true,
        });

        timer(environment.sessionGracePeriod)
          .pipe(takeUntil(this._destroy$))
          .subscribe(() => this._logout());

        dialogRef.afterClosed().subscribe(() => {
          this._logout();
        });
      });
  }

  private _resetIdleTimer(): void {
    if (this._idleTimer) {
      this._idleTimer.unsubscribe();
    }

    this._idleTimer = timer(environment.sessionTimeout)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        const timerSubscription = timer(environment.sessionGracePeriod)
          .pipe(takeUntil(this._destroy$))
          .subscribe(() => this._logout());

        const dialogRef = this._dialog.open(SessionTimeoutDialogComponent);

        dialogRef
          .afterClosed()
          .pipe(takeUntil(this._destroy$))
          .subscribe((value: boolean) => {
            if (!value) {
              timerSubscription.unsubscribe();
              this._resetIdleTimer();
            } else {
              this._logout();
            }
          });
      });
  }

  private _logout(): void {
    // Explicitly navigate to the logout page to destroy the session when the user has been idle for too long
    this._appInsights.trackEvent('UserLogout', {
      currentRoute: this._router.url,
      reason: 'UserLogout',
      timestamp: new Date().toISOString(),
    });
    console.warn('Session expired. Logging out user.');
    this._router.navigate(['/auth/logout']);
  }
}
